import { Adapter } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';
import { ADAPTER_URL, ADAPTERS_URL } from '../config/api-config';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export const useAdaptersApi = () => {
    const { getAuthToken } = useAuth();
    const getHeaders = createApiHeadersGetter(getAuthToken);
  
    const getAdapters = async (): Promise<Adapter[]> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${ADAPTERS_URL}`,
          {
            method: 'GET',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch adapters');
        }
        return response.json();
      } catch (error) {
        console.error('Error fetching Adapters:', error);
        throw error;
      }
    };

    const getAdapter = async (id: string): Promise<Adapter> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${ADAPTERS_URL}/${id}`,
          {
            method: 'GET',
            headers
          }
        );
        console.log('[getAdapter] Response:', response);
        
        if (!response.ok) {
          let errorMessage = 'Failed to fetch Adapter';
          let errorDetails;
          
          try {
            const errorData: APIError = await response.json();
            errorMessage = errorData.message || errorMessage;
            errorDetails = errorData.details;
          } catch (parseError) {
            errorMessage = response.statusText || errorMessage;
          }
          
          throw new ApiError(errorMessage, response.status, errorDetails);
        }
  
        return await response.json();
      } catch (error: any) {
        console.error('Error fetching Adapter:', error);
        if (error instanceof ApiError) {
          throw error;
        }
        throw new ApiError(error.message || 'Unknown error occurred', 500);
      }
    };

    const createAdapter = async (adapter: Omit<Adapter, 'id'>): Promise<Adapter> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(ADAPTERS_URL,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(adapter),
          }
        );
  
        if (!response.ok) {
          console.log('[createAdapter] Error creating adapter response:', response);
          try {
            const errorData: APIError = await response.json();
            console.log('[createAdapter] Error creating adapter errorData:', errorData);
            throw new Error(errorData.message || 'Failed to create adapter');
          } catch (parseError) {
            console.log('[createAdapter] Error parsing JSON:', String(parseError));
            throw new Error(String(parseError));
          }
        }
        return response.json();
      } catch (error) {
        console.error('Error creating Adapter:', error);
        throw error;
      }
    };
  
    const updateAdapter = async (adapter: Partial<Adapter>): Promise<Adapter> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${ADAPTERS_URL}/${adapter.adapterId}`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify(adapter),
          }
        );
        if (!response.ok) {
          throw new Error('Failed to update adapter');
        }
        return response.json();
      } catch (error) {
        console.error('Error updating Adapter:', error);
        throw error;
      }
    };
  
    const deleteAdapter = async (id: string): Promise<void> => {
      try {
        const headers = await getHeaders();
        const response = await fetch(`${ADAPTERS_URL}/${id}`,
          {
            method: 'DELETE',
            headers
          }
        );
        if (!response.ok) {
          throw new Error('Failed to delete Adapter');
        }
        return;
      } catch (error) {
        console.error('Error deleting Adapter:', error);
        throw error;
      }
    };

    const testAdapter = async (adapterName: string, parameters: any): Promise<any> => {
      try {
        console.log(parameters);
        const headers = await getHeaders();
        // NOTE: This is the only endpoint that uses the adapter name
        const response = await fetch(`${ADAPTER_URL}/${adapterName}`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(parameters),
          }
        );
        
        // Always try to parse the response as JSON, regardless of success or failure
        const data = await response.json().catch(err => {
          console.error('Error parsing response:', err);
          return { error: 'Failed to parse response' };
        });
        
        // Even if the response is not OK, return the exact data from the API
        // This ensures the raw API response body is passed back to the UI
        if (!response.ok) {
          console.error('Test adapter failed with status:', response.status);
          console.error('Error response data:', data);
          return data; // Return the raw API response body
        }
        
        // Return the data exactly as received from the API, without transforming it
        return data;
      } catch (error) {
        console.error('Error testing Adapter:', error);
        // For network errors or other exceptions, create an error object
        return {
          error: true,
          message: error instanceof Error ? error.message : 'Unknown error occurred'
        };
      }
    };
  
    return {
      getAdapters,
      getAdapter,
      createAdapter,
      updateAdapter,
      deleteAdapter,
      testAdapter
    };
  }; 